import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Envelope, 
  Lock, 
  ArrowRight, 
  CircleNotch,
  CheckCircle,
  Briefcase,
  ShieldCheck,
  Receipt,
  ClipboardText,
  ChartLineUp,
  Users,
  CurrencyDollar,
  Warehouse,
  Buildings,
  Cloud,
  Clock,
  LockKey,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAccountantMode, setIsAccountantMode] = useState(searchParams.get('mode') === 'accountant');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      const u = result; // login() returns userData directly
      // Platform super-admins (sgkapps.in@gmail.com or any user owning the platform
      // company) should land directly on the Platform Admin Dashboard, NOT the
      // tenant operational dashboard.
      const isPlatformAdmin =
        (u?.email || '').toLowerCase() === 'sgkapps.in@gmail.com' ||
        u?.is_platform_admin === true;
      if (isPlatformAdmin) {
        navigate('/dashboard/platform-admin');
      } else if (isAccountantMode) {
        navigate('/dashboard/accountant');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  // SME Features
  const smeFeatureCards = [
    { icon: Users, label: 'HR Management', color: '#3b82f6' },
    { icon: CurrencyDollar, label: 'Accounts & Finance', color: '#10b981' },
    { icon: ChartLineUp, label: 'Sales & CRM', color: '#f59e0b' },
    { icon: Warehouse, label: 'Inventory', color: '#ec4899' },
  ];

  // Accountant Features
  const accountantFeatureCards = [
    { icon: Buildings, label: 'Multi-Client Dashboard', color: '#d4a84b' },
    { icon: Receipt, label: 'VAT Filing', color: '#10b981' },
    { icon: ShieldCheck, label: 'Tax Compliance', color: '#8b5cf6' },
    { icon: ClipboardText, label: 'Bulk Reports', color: '#3b82f6' },
  ];

  // SME Benefits
  const smeBenefits = [
    'Accounting & payroll management',
    'Invoicing & sales tracking',
    'Compliance tracking & alerts',
    'Reports & analytics dashboard',
  ];

  // Accountant Benefits
  const accountantBenefits = [
    'Multi-company management',
    'Bulk VAT filing & reporting',
    'Tax compliance safety engine',
    'Deadline tracking & alerts',
  ];

  const featureCards = isAccountantMode ? accountantFeatureCards : smeFeatureCards;
  const benefits = isAccountantMode ? accountantBenefits : smeBenefits;

  return (
    <div className="min-h-screen flex bg-[#0a1628]">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />

      {/* LEFT PANEL - Form Area */}
      <div className="w-full lg:w-[52%] flex items-center justify-center p-6 sm:p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          
          {/* Logo Section */}
          <div className="text-center mb-10">
            <div className="inline-block">
              {/* Logo */}
              <div className="flex items-center justify-center gap-3 mb-3">
                {/* Hexagonal BizCore Mark */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden ${
                  isAccountantMode
                    ? 'bg-[#0a0a0a] ring-1 ring-[#d4a84b]/60'
                    : 'bg-[#0a0a0a] ring-1 ring-[#d4a84b]/30'
                }`}>
                  <img
                    src={isAccountantMode ? '/bizcore-pro-mark-192.png' : '/bizcore-mark-192.png'}
                    alt={isAccountantMode ? 'BizCore Pro' : 'BizCore'}
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                {/* Text Logo */}
                <div>
                  <h1 className="text-4xl font-black tracking-tight">
                    <span className="text-white font-black">BIZ</span>
                    <span className="text-white italic font-medium">Core</span>
                    {isAccountantMode && (
                      <span className="text-[#d4a84b] font-normal ml-1">Pro</span>
                    )}
                    <span className="text-[#d4a84b] text-sm align-super ml-0.5">™</span>
                  </h1>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                by <span className="font-semibold text-white">SGK</span>
                <span className="font-semibold text-[#d4a84b]"> Apps</span>
                <span className="text-slate-500">™</span>
              </p>
            </div>
          </div>

          {/* Dynamic Headline */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              {isAccountantMode ? 'Welcome Back' : 'Welcome Back'}
            </h2>
            <p className="text-slate-400 text-sm sm:text-base">
              {isAccountantMode 
                ? 'Access your multi-client dashboard' 
                : 'Sign in to your account to continue'}
            </p>
          </div>

          {/* Account Type Toggle */}
          <div className="space-y-3">
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-400 text-center">
              Select Account Type
            </label>
            <div className="flex justify-center">
              <div className="inline-flex rounded-2xl bg-[#1e293b]/80 p-1.5 border border-slate-700/50 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => setIsAccountantMode(false)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                    !isAccountantMode 
                      ? 'bg-white shadow-lg text-[#1e3a5f]' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="toggle-sme"
                >
                  <Buildings size={18} weight={!isAccountantMode ? 'fill' : 'regular'} />
                  <span>Business (SME)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsAccountantMode(true)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                    isAccountantMode 
                      ? 'bg-gradient-to-r from-[#d4a84b] to-[#c49a3d] shadow-lg text-[#0f172a]' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="toggle-accountant"
                >
                  <Briefcase size={18} weight={isAccountantMode ? 'fill' : 'regular'} />
                  <span>Accountant (Pro)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div 
              className="p-4 rounded-xl text-sm bg-red-500/10 text-red-400 border border-red-500/20 backdrop-blur-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                Email Address
              </Label>
              <div className="relative">
                <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 pr-11 h-12 bg-[#1e293b]/60 border-slate-700/50 text-white placeholder:text-slate-500 rounded-xl focus:border-[#d4a84b] focus:ring-[#d4a84b]/20 transition-all"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#d4a84b] p-1 rounded transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-14 font-semibold text-base transition-all duration-300 rounded-xl bg-gradient-to-r from-[#d4a84b] to-[#c49a3d] hover:from-[#e5b95c] hover:to-[#d4a84b] shadow-lg shadow-[#d4a84b]/20 hover:shadow-xl hover:shadow-[#d4a84b]/30 hover:-translate-y-0.5"
              style={{ color: '#0f172a' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <CircleNotch className="animate-spin" size={22} />
              ) : (
                <>
                  {isAccountantMode ? 'Sign In to Pro' : 'Sign In'}
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </Button>
          </form>

          {/* Recovery Links */}
          <div className="flex items-center justify-center gap-4 text-sm">
            <Link to="/forgot-password" className="text-slate-400 hover:text-[#d4a84b] transition-colors" data-testid="forgot-password-link">
              Forgot Password?
            </Link>
            <span className="text-slate-600">|</span>
            <Link to="/forgot-username" className="text-slate-400 hover:text-[#d4a84b] transition-colors" data-testid="forgot-username-link">
              Forgot Username?
            </Link>
          </div>

          {/* Register Link */}
          <p className="text-center text-sm text-slate-400">
            Don't have an account?{' '}
            <Link 
              to={isAccountantMode ? "/register?mode=accountant" : "/register"}
              className="font-semibold text-[#d4a84b] hover:text-white transition-colors"
              data-testid="register-link"
            >
              Create one
            </Link>
          </p>

          {/* Footer Trust Indicators */}
          <div className="flex items-center justify-center gap-6 pt-6 border-t border-slate-700/30">
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <LockKey size={14} className="text-[#d4a84b]" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <Cloud size={14} className="text-[#d4a84b]" />
              <span>Cloud-Based</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <ShieldCheck size={14} className="text-[#d4a84b]" />
              <span>UAE Compliance Ready</span>
            </div>
          </div>

          {/* Website Footer */}
          <div className="text-center pt-4">
            <a 
              href="https://bizcore.sgkapps.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-[#d4a84b] transition-colors"
            >
              bizcore.sgkapps.com
            </a>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL - Value Proposition */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden">
        {/* Gradient Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: isAccountantMode 
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
              : 'linear-gradient(135deg, #1e3a5f 0%, #234b70 50%, #2d5a80 100%)'
          }}
        />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #d4a84b 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-10 xl:px-14 py-12 w-full">
          
          {/* Mode Badge */}
          {isAccountantMode && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4a84b]/20 text-[#d4a84b] text-xs font-semibold mb-6 w-fit">
              <Briefcase size={14} weight="fill" />
              ACCOUNTANT EDITION
            </div>
          )}

          {/* Title */}
          <h2 className="text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
            {isAccountantMode 
              ? <>Your Clients Await</>
              : <>Your Business<br />Command Center</>}
          </h2>
          
          <p className={`text-base mb-8 ${isAccountantMode ? 'text-slate-300' : 'text-blue-100'}`}>
            {isAccountantMode 
              ? 'Access all your client companies in one powerful dashboard'
              : 'All-in-one ERP solution to streamline your operations'}
          </p>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {featureCards.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 backdrop-blur-sm rounded-xl p-4 border transition-all duration-300 hover:scale-[1.02] cursor-default"
                style={{
                  background: isAccountantMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                  borderColor: isAccountantMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.15)'
                }}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon size={22} style={{ color: feature.color }} weight="duotone" />
                </div>
                <span className="text-white text-sm font-medium">{feature.label}</span>
              </div>
            ))}
          </div>

          {/* Benefits List */}
          <div className="space-y-3 mb-10">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#d4a84b]/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={14} className="text-[#d4a84b]" weight="fill" />
                </div>
                <span className={`text-sm ${isAccountantMode ? 'text-slate-300' : 'text-blue-100'}`}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>

          {/* Trust Stats */}
          <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Cloud size={18} className="text-[#d4a84b]" weight="duotone" />
                <span className="text-xl font-bold text-white">100%</span>
              </div>
              <div className={`text-xs ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>Cloud-Based</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Clock size={18} className="text-[#d4a84b]" weight="duotone" />
                <span className="text-xl font-bold text-white">24/7</span>
              </div>
              <div className={`text-xs ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>Availability</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <LockKey size={18} className="text-[#d4a84b]" weight="duotone" />
                <span className="text-xl font-bold text-white">SSL</span>
              </div>
              <div className={`text-xs ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>Secured</div>
            </div>
          </div>

          {/* Bottom Branding */}
          <div className="mt-auto pt-8">
            <div className={`flex items-center gap-2 text-sm ${isAccountantMode ? 'text-slate-400' : 'text-blue-200'}`}>
              <div className="w-8 h-[1px] bg-current opacity-30"></div>
              <span>{isAccountantMode ? 'For accounting professionals' : 'Trusted by businesses'}</span>
            </div>
            <div className="mt-6 text-center">
              <a 
                href="https://bizcore.sgkapps.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-slate-400 hover:text-[#d4a84b] transition-colors"
              >
                bizcore.sgkapps.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
